import { Color, EventDispatcher, PCFSoftShadowMap, Scene, WebGLRenderer } from 'three';
import { autoInjectable, container, singleton } from 'tsyringe';
import Sizes from '../Utils/Sizes';
import Time from '../Utils/Time';
import VrScene, { Rooms } from './Three/VrScene';
import { InteractionManager } from './Controllers/InteractionManager';
import Stats from 'three/examples/jsm/libs/stats.module';
import { SRGBColorSpace } from 'three/src/constants';
import Camera from "../Camera";

// Define the event map for Renderer
export interface RendererEventMap {
    sceneChange: { scene: VrScene | Scene };
    resize: { width: number; height: number };
}

@singleton()
@autoInjectable()
export default class Renderer extends EventDispatcher<RendererEventMap> {
    public webGLRenderer: WebGLRenderer;

    public scene: Scene | VrScene = new Scene();

    private onChangeCallbacks: ((scene: any) => void)[] = [];

    public interactionManager: InteractionManager;

    public constructor(public sizes?: Sizes, public time?: Time) {
        super();
    }

    public get isClassroom(): boolean {
        return this.scene.name === 'classroom';
    }

    public get isGroup(): boolean {
        return this.scene.name === 'Group';
    }

    public get isPrivateScene(): boolean {
        return this.scene.name === 'PrivateRoom';
    }

    public isSceneType(sceneName: string): boolean {
        return this.scene.name === sceneName;
    }

    public startRenderLoop(scene: any, camera) {
        this.scene = scene;

        this.time.on('tick', () => {
            this.webGLRenderer.render(this.scene, camera);
        });
    }

    public changeRenderLoopScene(scene) {
        this.onChangeCallbacks.forEach((callback) => callback(scene));

        this.dispatchEvent({ type: 'sceneChange', scene });

        this.scene = scene;
    }

    public onSceneChange(callback: (scene: any) => void) {
        this.onChangeCallbacks.push(callback);
    }

    public endWebXRSession() {
        if (this.webGLRenderer.xr.isPresenting) {
            this.webGLRenderer.xr
                .getSession()
                .end()
                .then(() => {
                    console.log('WebXR session ended');
                })
                .catch((error) => {
                    console.error('Error ending WebXR session:', error);
                });
        } else {
            console.log('No active WebXR session to disable');
        }
    }

    public async joinAndLeaveRoom(room: Rooms) { }

    public init(canvas: HTMLCanvasElement) {
        this.webGLRenderer = new WebGLRenderer({
            canvas,
            antialias: true,
            alpha: false,
            powerPreference: 'high-performance',
            precision: 'highp',
            depth: true,
            // @ts-ignore
            xrCompatible: true,
        });

        const { width, height } = this.sizes.viewport;

        this.webGLRenderer.setSize(width, height);
        const maxPixelRatio = 1;
        const renderScaleFactor = 1;

        this.webGLRenderer.setPixelRatio(Math.min(window.devicePixelRatio * renderScaleFactor, maxPixelRatio));
        this.webGLRenderer.setAnimationLoop(this.time.tick);
        this.webGLRenderer.setClearColor(new Color('gray'));
        this.webGLRenderer.xr.enabled = true;
        this.webGLRenderer.xr.setReferenceSpaceType('local');
        this.webGLRenderer.localClippingEnabled = true
        this.webGLRenderer.shadowMap.enabled = true;
        // this.webGLRenderer.shadowMap.type = PCFSoftShadowMap;
        //@ts-ignore
        this.webGLRenderer.xr.setFramebufferScaleFactor(1.2);
        this.webGLRenderer.outputColorSpace = SRGBColorSpace;

        //
        this.sizes.on('resize', () => {
            const { width, height } = this.sizes.viewport;
            this.webGLRenderer.setSize(width, height);
            // this.webGLRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

            this.dispatchEvent({
                type: 'resize',
                width,
                height
            });
        });

    }

    public resetAfterHMR() {
        console.log('🔄 Resetting Renderer after HMR...');


        const { width, height } = this.sizes.viewport;

        try {
            const gl = this.webGLRenderer.getContext();
            if (gl.isContextLost()) {
                console.warn('⚠️ WebGL context was lost, restoring...');
                this.webGLRenderer.forceContextRestore();
            }
        } catch (e) {
            console.warn('Could not check context state:', e);
        }

        this.webGLRenderer.setSize(width, height);
        this.webGLRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

        this.webGLRenderer.clear();

        if (this.scene && this.scene.children.length > 0) {
            try {
                const camera = container.resolve(Camera);
                this.webGLRenderer.render(this.scene, camera.instance);
            } catch (e) {
                console.warn('Initial render failed:', e);
            }
        }

        console.log('✅ Renderer reset complete (context preserved)', {
            size: { width, height },
            contextLost: this.webGLRenderer.getContext().isContextLost(),
        });
    }
}
